import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, Wand2 } from "lucide-react";

const readStream = async (res, onDelta) => {
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const line = p.replace(/^data: /, "");
      if (line === "[DONE]") return;
      const msg = JSON.parse(line);
      if (msg.error) throw new Error(msg.error);
      onDelta(msg.t || "");
    }
  }
};

export const AiWriter = ({ user, service, onStart, onDelta }) => {
  const [open, setOpen] = useState(false);
  const [idea, setIdea] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const write = async () => {
    setBusy(true); setErr("");
    try {
      const token = sessionStorage.getItem("kag_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/brief`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ idea, service }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d?.detail === "string" ? d.detail : "Couldn't reach the AI writer. Please try again.");
      }
      onStart();
      await readStream(res, onDelta);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button type="button" data-testid="ai-writer-open-btn" onClick={() => setOpen(true)}
        className="sm:col-span-2 group flex items-center gap-3 border border-dashed border-[var(--arcane)]/60 px-5 py-4 text-left hover:bg-[var(--arcane)]/10 transition-colors">
        <Sparkles className="w-5 h-5 text-[var(--arcane)] transition-transform group-hover:rotate-12" />
        <span>
          <span className="block font-display text-base">Not sure what to write?</span>
          <span className="block text-sm text-[var(--ink-dim)]">Describe your idea in a few words and AI will write the request for you.</span>
        </span>
      </button>
    );
  }

  return (
    <div data-testid="ai-writer" className="sm:col-span-2 border border-[var(--arcane)]/60 bg-[var(--arcane)]/5 p-5 space-y-3">
      <div className="flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-[var(--arcane)]">
        <Wand2 className="w-4 h-4" /> AI request writer
      </div>
      {user ? (
        <>
          <textarea rows={2} data-testid="ai-idea-input" className="field resize-none" maxLength={1000}
            placeholder="e.g. dark vampire logo for my clothing brand, red and gold, with a crown"
            value={idea} onChange={(e) => setIdea(e.target.value)} />
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" disabled={busy || idea.trim().length < 3} data-testid="ai-write-btn" onClick={write}
              className="btn-ghost px-5 py-3 font-mono text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? "Writing…" : "Write it for me"}
            </button>
            <span className="text-xs text-[var(--ink-dim)]">It fills in the box below. You can edit it before sending.</span>
          </div>
          {err && <p data-testid="ai-writer-error" className="text-sm text-[var(--blood)]">{err}</p>}
        </>
      ) : (
        <p data-testid="ai-writer-login-note" className="text-sm text-[var(--ink-dim)]">
          The AI writer is free for members. <Link to="/account" data-testid="ai-writer-login-link" className="text-[var(--gold)] underline underline-offset-4">Log in or create a free profile</Link> to use it.
        </p>
      )}
    </div>
  );
};
